import React, { useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

import {
  getAvatarColor,
  getInitials,
  truncateName,
} from './projectInvitationPreviewShared';

/* ─── sample data ──────────────────────────────────────────────────────────── */

const SAMPLE_PROJECT = {
  name: 'Raintree Hollow',
  description:
    'This project involves a full residential home renovation aimed at improving functionality, comfort, safety, and visual appeal of the property. Work includes structural repairs, updated plumbing and electrical, new flooring, and modern finishes throughout.',
  address: '11 N Raintree Hollow Ln, Houston, Texas 77027, USA',
  teamName: 'Aquaworks Construction',
  usersCount: 3,
  members: [
    { id: '1', role: 'OWNER', name: 'Alex Smith', avatar: null },
    { id: '2', role: 'MEMBER', name: 'Chelsea Brown', avatar: null },
    { id: '3', role: 'MEMBER', name: 'Chelita Rivera', avatar: null },
  ],
};

/* ─── sub-components ──────────────────────────────────────────────────────── */

type MemberAvatarProps = {
  name: string | null;
  index: number;
  size?: number;
};
const MemberAvatar: React.FC<MemberAvatarProps> = ({
  name,
  index,
  size = 52,
}) => (
  <View
    style={[
      styles.memberAvatar,
      {
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: getAvatarColor(index),
      },
    ]}>
    <Text style={[styles.memberInitials, { fontSize: size * 0.3 }]}>
      {getInitials(name)}
    </Text>
  </View>
);

const BlackIconBox: React.FC<{
  iconName: React.ComponentProps<typeof Icon>['name'];
}> = ({ iconName }) => (
  <View style={styles.blackIconBox}>
    <Icon name={iconName} width={18} height={18} color='white' />
  </View>
);

/* ─── main screen ─────────────────────────────────────────────────────────── */

const ProjectInvitationPreviewScreenDummy: React.FC = () => {
  const [descExpanded, setDescExpanded] = useState(false);
  const [joinModalVisible, setJoinModalVisible] = useState(false);

  const { name, description, address, teamName, usersCount, members } =
    SAMPLE_PROJECT;

  const owner = members.find((m) => m.role === 'OWNER') ?? members[0] ?? null;

  return (
    <>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        {/* ── 1. Project details card (purple) ─────────────────────────── */}
        <View style={styles.headerCard}>
          {/* Project icon + name */}
          <View style={styles.headerTitleRow}>
            <View style={styles.projectIconBox}>
              <Icon
                name='BuildingHomeHouse'
                width={18}
                height={18}
                color='white'
              />
            </View>
            <Text style={styles.projectName} numberOfLines={2}>
              {name}
            </Text>
          </View>

          {/* Address */}
          {!!address && <Text style={styles.addressText}>{address}</Text>}

          {/* Team | Owner row */}
          <View style={styles.metaRow}>
            {/* Team */}
            <View style={styles.metaHalf}>
              <View style={styles.teamIconBox}>
                <Icon name='TT' width={14} height={14} color='white' />
              </View>
              <Text style={styles.metaText} numberOfLines={1}>
                {truncateName(teamName, 16)}
              </Text>
            </View>

            {/* Owner */}
            {owner && (
              <View style={styles.metaHalf}>
                <View
                  style={[
                    styles.ownerAvatar,
                    { backgroundColor: getAvatarColor(0) },
                  ]}>
                  <Text style={styles.ownerInitials}>
                    {getInitials(owner.name)}
                  </Text>
                </View>
                <Text style={styles.metaText} numberOfLines={1}>
                  {owner.name}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── 2. Description card ───────────────────────────────────────── */}
        {!!description && (
          <View style={styles.card}>
            <View style={styles.descHeaderRow}>
              <Text style={styles.sectionTitle}>Description</Text>
              <TouchableOpacity
                onPress={() => setDescExpanded((v) => !v)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={styles.seeMoreText}>
                  {descExpanded ? 'See Less' : 'See More'}
                </Text>
              </TouchableOpacity>
            </View>
            <Text
              style={styles.descriptionText}
              numberOfLines={descExpanded ? undefined : 3}>
              {description}
            </Text>
          </View>
        )}

        {/* ── 3. CTA card (dark) ────────────────────────────────────────── */}
        <View style={styles.ctaCard}>
          <View style={styles.ctaIconCircle}>
            <Icon name='Share' width={20} height={20} color='white' />
          </View>
          <Text style={styles.ctaHeading}>
            Someone shared this project with you
          </Text>
          <Text style={styles.ctaSubtitle}>
            The project owner is waiting for your request.
          </Text>
          <TouchableOpacity
            style={styles.joinButton}
            onPress={() => setJoinModalVisible(true)}
            activeOpacity={0.85}>
            <Text style={styles.joinButtonLabel}>Request to Join</Text>
          </TouchableOpacity>
        </View>

        {/* ── 4. Members ───────────────────────────────────────────────── */}
        <View style={styles.membersSection}>
          <Text style={styles.sectionTitle}>Members ({usersCount})</Text>
          <View style={styles.membersRow}>
            {members.map((member, index) => (
              <View key={member.id} style={styles.memberItem}>
                <MemberAvatar name={member.name} index={index} size={52} />
                <Text style={styles.memberName}>
                  {truncateName(member.name)}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── 5. Feature cards ─────────────────────────────────────────── */}
        {/* Checklist + Tasks wrapped in a single rounded card */}
        <View style={styles.featureListCard}>
          {/* Checklist */}
          <View style={[styles.featureRow, styles.featureRowBorder]}>
            <BlackIconBox iconName='FileText' />
            <View style={styles.featureRowContent}>
              <Text style={styles.featureTitle}>Checklist</Text>
              <Text style={styles.featureSubtitle}>
                Start faster with a project template.
              </Text>
            </View>
            <Icon name='ChevronRight' width={20} height={20} color='grey03' />
          </View>

          {/* Tasks */}
          <View style={styles.featureRow}>
            <BlackIconBox iconName='TTHashtag' />
            <View style={styles.featureRowContent}>
              <Text style={styles.featureTitle}>Tasks</Text>
            </View>
            <Icon name='ChevronRight' width={20} height={20} color='grey03' />
          </View>
        </View>

        {/* Activity Log + Files & Media (2-column) */}
        <View style={styles.featureGrid}>
          <View
            style={[styles.featureGridCell, { marginRight: theme.spacing.s }]}>
            <BlackIconBox iconName='ActivityDot' />
            <Text style={[styles.featureTitle, { marginTop: theme.spacing.s }]}>
              Activity Log
            </Text>
          </View>
          <View style={styles.featureGridCell}>
            <BlackIconBox iconName='FileImage' />
            <Text style={[styles.featureTitle, { marginTop: theme.spacing.s }]}>
              Files &amp; Media
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      <Modal
        visible={joinModalVisible}
        transparent
        animationType='fade'
        onRequestClose={() => setJoinModalVisible(false)}>
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setJoinModalVisible(false)}>
          <Pressable
            style={styles.modalCard}
            onPress={() => {
              /* swallow */
            }}>
            {/* Close */}
            <TouchableOpacity
              style={styles.modalClose}
              onPress={() => setJoinModalVisible(false)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Text style={styles.modalCloseText}>✕</Text>
            </TouchableOpacity>

            {/* Title */}
            <Text style={styles.modalTitle}>Create an account</Text>
            <Text style={styles.modalSubtitle}>
              Someone shared this project with you
            </Text>

            {/* Project info box */}
            <View style={styles.modalProjectBox}>
              <Text style={styles.modalProjectName}>{name}</Text>
              <View style={styles.modalProjectAddress}>
                <Icon name='Pin' width={12} height={12} color='textSecondary' />
                <Text style={styles.modalProjectAddressText} numberOfLines={1}>
                  {address}
                </Text>
              </View>
              <Text style={styles.modalRoleText}>
                {'Your Role : '}
                <Text style={styles.modalRoleBold}>Viewer </Text>
                <Text style={styles.modalRolePending}>(Pending Approval)</Text>
              </Text>
            </View>

            {/* Social buttons */}
            <View style={styles.socialRow}>
              <TouchableOpacity style={styles.socialButton} activeOpacity={0.8}>
                <Text style={styles.socialIcon}>G</Text>
                <Text style={styles.socialLabel}>Google</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.socialButton, { marginLeft: theme.spacing.s }]}
                activeOpacity={0.8}>
                <Text style={styles.socialIconApple}></Text>
                <Text style={styles.socialLabel}>Apple</Text>
              </TouchableOpacity>
            </View>

            {/* Divider */}
            <View style={styles.dividerRow}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>or</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Email button */}
            <TouchableOpacity style={styles.emailButton} activeOpacity={0.85}>
              <Text style={styles.emailButtonLabel}>Sign up with email</Text>
            </TouchableOpacity>

            {/* Login link */}
            <View style={styles.loginRow}>
              <Text style={styles.loginPrompt}>Already have an account? </Text>
              <TouchableOpacity>
                <Text style={styles.loginLink}>Log In</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
};

/* ─── styles ──────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: theme.colors.grey01,
  },
  content: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.xxl,
  },

  /* Header card */
  headerCard: {
    backgroundColor: theme.colors.purple,
    borderRadius: theme.borderRadii.l,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  projectIconBox: {
    width: 36,
    height: 36,
    borderRadius: theme.borderRadii.xs,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.s,
  },
  projectName: {
    flex: 1,
    color: theme.colors.white,
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 26,
  },
  addressText: {
    color: theme.colors.white,
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.85,
    marginBottom: theme.spacing.s,
  },
  metaRow: {
    flexDirection: 'row',
    marginTop: theme.spacing.xs,
  },
  metaHalf: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  teamIconBox: {
    width: 26,
    height: 26,
    borderRadius: theme.borderRadii.xxs,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.xs,
  },
  ownerAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginRight: theme.spacing.xs,
  },
  ownerInitials: {
    color: theme.colors.white,
    fontSize: 10,
    fontWeight: '700',
  },
  metaText: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },

  /* White cards */
  card: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  descHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  seeMoreText: {
    fontSize: 13,
    color: theme.colors.grey04,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.colors.textSecondary,
  },

  /* CTA card */
  ctaCard: {
    backgroundColor: theme.colors.black,
    borderRadius: theme.borderRadii.m,
    padding: theme.spacing.m,
    alignItems: 'center',
    marginBottom: theme.spacing.s,
  },
  ctaIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.greenSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.s,
  },
  ctaHeading: {
    color: theme.colors.white,
    fontSize: 17,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
    lineHeight: 24,
  },
  ctaSubtitle: {
    color: theme.colors.grey04,
    fontSize: 13,
    textAlign: 'center',
    marginBottom: theme.spacing.m,
  },
  joinButton: {
    backgroundColor: theme.colors.greenBrand,
    borderRadius: theme.borderRadii.xl,
    paddingVertical: theme.spacing.s,
    width: '100%',
    alignItems: 'center',
  },
  joinButtonLabel: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },

  /* Members */
  membersSection: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  membersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: theme.spacing.s,
  },
  memberItem: {
    alignItems: 'center',
    marginRight: theme.spacing.m,
    marginBottom: theme.spacing.xs,
    width: 52,
  },
  memberAvatar: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  memberInitials: {
    color: theme.colors.white,
    fontWeight: '700',
  },
  memberName: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },

  /* Feature rows */
  featureListCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    overflow: 'hidden',
    marginBottom: theme.spacing.s,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  featureRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
  },
  featureRowContent: {
    flex: 1,
    marginLeft: theme.spacing.s,
  },
  featureTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  featureSubtitle: {
    fontSize: 13,
    color: theme.colors.grey04,
    marginTop: 2,
  },
  featureGrid: {
    flexDirection: 'row',
    marginTop: theme.spacing.s,
    marginBottom: theme.spacing.s,
  },
  featureGridCell: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    padding: theme.spacing.m,
  },
  blackIconBox: {
    width: 36,
    height: 36,
    borderRadius: theme.borderRadii.xs,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* Modal */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.m,
  },
  modalCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.l,
    padding: theme.spacing.l,
    width: '100%',
    maxWidth: 400,
  },
  modalClose: {
    position: 'absolute',
    top: theme.spacing.m,
    right: theme.spacing.m,
  },
  modalCloseText: {
    fontSize: 18,
    color: theme.colors.grey04,
    fontWeight: '400',
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginTop: theme.spacing.xs,
  },
  modalSubtitle: {
    fontSize: 13,
    color: theme.colors.grey04,
    textAlign: 'center',
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.m,
  },
  modalProjectBox: {
    backgroundColor: theme.colors.purpleMild,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.m,
  },
  modalProjectName: {
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  modalProjectAddress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.s,
  },
  modalProjectAddressText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    marginLeft: 4,
    flex: 1,
  },
  modalRoleText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  modalRoleBold: {
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  modalRolePending: {
    color: theme.colors.grey04,
  },
  socialRow: {
    flexDirection: 'row',
    marginBottom: theme.spacing.m,
  },
  socialButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.colors.grey02,
    borderRadius: theme.borderRadii.xs,
    paddingVertical: theme.spacing.s,
  },
  socialIcon: {
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.blue,
    marginRight: theme.spacing.xs,
  },
  socialIconApple: {
    fontSize: 16,
    color: theme.colors.black,
    marginRight: theme.spacing.xs,
  },
  socialLabel: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.m,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.grey02,
  },
  dividerText: {
    fontSize: 13,
    color: theme.colors.grey04,
    marginHorizontal: theme.spacing.s,
  },
  emailButton: {
    borderWidth: 1,
    borderColor: theme.colors.greenBrand,
    borderRadius: theme.borderRadii.xl,
    paddingVertical: theme.spacing.s,
    alignItems: 'center',
    marginBottom: theme.spacing.m,
  },
  emailButtonLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.greenBrand,
  },
  loginRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  loginPrompt: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  loginLink: {
    fontSize: 14,
    color: theme.colors.greenBrand,
    fontWeight: '600',
  },
});

export default ProjectInvitationPreviewScreenDummy;
